import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Share2, Sparkles } from "lucide-react";
import { ScriptureCard } from "@/components/ScriptureCard";
import { useAuth } from "@/context/AuthContext";
import { isPremium } from "@/components/PremiumGate";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function Scripture() {
    const { user } = useAuth();
    const premium = isPremium(user);
    const [today, setToday] = useState(null);
    const [all, setAll] = useState([]);
    const [shareOpen, setShareOpen] = useState(false);
    const [shareScripture, setShareScripture] = useState(null);
    const [companion, setCompanion] = useState(null);
    const [companionBusy, setCompanionBusy] = useState(false);

    useEffect(() => {
        api.get("/scripture/today").then((r) => setToday(r.data));
        api.get("/scripture/all").then((r) => setAll(r.data));
    }, []);

    const openShare = (s) => { setShareScripture(s); setShareOpen(true); };

    const openCompanion = async (reference) => {
        if (!premium) return;
        setCompanionBusy(true);
        setCompanion(null);
        try {
            const { data } = await api.get(`/scripture/companion`, { params: { reference } });
            setCompanion({ reference, ...data });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't open the companion.");
        } finally {
            setCompanionBusy(false);
        }
    };

    if (!today) return <div className="text-muted-foreground py-16">Opening today's scripture…</div>;

    return (
        <div className="space-y-20">
            <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="max-w-3xl"
            >
                <div className="eyebrow-gold mb-4">Today · KJV</div>
                <blockquote className="font-serif text-4xl md:text-5xl leading-[1.1] italic">
                    &ldquo;{today.verse}&rdquo;
                </blockquote>
                <div className="mt-6 text-sm uppercase tracking-widest text-[hsl(var(--eden-gold))]" data-testid="scripture-reference">
                    {today.reference}
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                    <Button
                        onClick={() => openShare(today)}
                        data-testid="scripture-share-btn"
                        variant="outline"
                        className="rounded-md border-foreground/15"
                    >
                        <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Create a sharing card
                    </Button>
                    {premium && (
                        <Button
                            onClick={() => openCompanion(today.reference)}
                            disabled={companionBusy}
                            data-testid="scripture-companion-btn"
                            className="bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 rounded-md"
                        >
                            <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            {companionBusy ? "Considering…" : "Open the Companion"}
                        </Button>
                    )}
                </div>

                <div className="mt-12 grid md:grid-cols-3 gap-8">
                    <div>
                        <div className="eyebrow mb-2">Reflection</div>
                        <p className="text-sm leading-relaxed">{today.reflection}</p>
                    </div>
                    <div>
                        <div className="eyebrow mb-2">Stewardship focus</div>
                        <p className="text-sm leading-relaxed">{today.focus}</p>
                    </div>
                    <div>
                        <div className="eyebrow mb-2">A prayer</div>
                        <p className="text-sm leading-relaxed italic">{today.prayer}</p>
                    </div>
                </div>
            </motion.section>

            {/* Scripture Companion panel (Premium) */}
            {companion && (
                <motion.section
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="hairline rounded-lg p-10 md:p-14 max-w-3xl"
                    data-testid="scripture-companion-panel"
                >
                    <div className="eyebrow-gold mb-4">Scripture Companion · {companion.reference}</div>
                    <div className="space-y-8 prose-cultiv">
                        <div>
                            <div className="eyebrow mb-2">Context</div>
                            <p>{companion.context}</p>
                        </div>
                        <div>
                            <div className="eyebrow mb-2">Principle</div>
                            <p className="font-serif text-xl leading-snug">{companion.principle}</p>
                        </div>
                        <div>
                            <div className="eyebrow mb-2">Why it applies</div>
                            <p>{companion.why_it_applies}</p>
                        </div>
                        <div>
                            <div className="eyebrow mb-2">Reflection questions</div>
                            <ol className="space-y-3 list-none pl-0">
                                {(companion.reflection_questions || []).map((q, i) => (
                                    <li key={i} className="flex gap-4 items-baseline">
                                        <span className="font-serif text-lg text-[hsl(var(--eden-gold))]/70">{String(i + 1).padStart(2, "0")}</span>
                                        <span>{q}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                        <div>
                            <div className="eyebrow mb-2">This week</div>
                            <p className="font-serif text-xl leading-snug">{companion.application}</p>
                        </div>
                    </div>
                </motion.section>
            )}

            {!premium && (
                <section className="hairline rounded-lg p-8 max-w-3xl">
                    <div className="eyebrow-gold mb-2">Scripture Companion</div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        Premium adds a full biblical companion to every verse — context, the principle being taught,
                        why it applies to your current stewardship, three reflection questions, and one concrete
                        application for the week.{" "}
                        <Link to="/membership" className="text-[hsl(var(--eden-gold))] hover:underline">Continue cultivating &rarr;</Link>
                    </p>
                </section>
            )}

            <section>
                <div className="eyebrow mb-6">Library</div>
                <div className="grid md:grid-cols-2 gap-4">
                    {all.map((s) => (
                        <div key={s.day_index} className="hairline rounded-md p-6 group" data-testid={`scripture-item-${s.day_index}`}>
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-[hsl(var(--eden-gold))] mb-2">{s.reference}</div>
                                    <div className="font-serif text-lg leading-snug italic">&ldquo;{s.verse}&rdquo;</div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <button
                                        onClick={() => openShare(s)}
                                        className="opacity-60 hover:opacity-100 transition-opacity"
                                        aria-label="Share this verse"
                                        data-testid={`scripture-share-item-${s.day_index}`}
                                    >
                                        <Share2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                    {premium && (
                                        <button
                                            onClick={() => openCompanion(s.reference)}
                                            className="opacity-60 hover:opacity-100 transition-opacity text-[hsl(var(--eden-gold))]"
                                            aria-label="Open Companion"
                                            data-testid={`scripture-companion-item-${s.day_index}`}
                                        >
                                            <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <ScriptureCard
                scripture={shareScripture}
                open={shareOpen}
                onOpenChange={setShareOpen}
            />
        </div>
    );
}
